import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, ChevronRight, ChevronLeft, Navigation, ChevronDown, Dumbbell, Users, Award, Plus, Minus, ImageOff, Star } from 'lucide-react';
import type { StateSlug } from '../lib/stateCore.mjs';
import { SeoHead } from '../components/SeoHead';
import { getStaticPageMetadata } from '../lib/seo';
import { FilterState } from '../types';
import { STYLE_OPTIONS, AGE_OPTIONS, EXPERIENCE_OPTIONS } from '../lib/filters';
import { getLocationSuggestions, resolveNearestLocation } from '../services/locationService';
import { normalizeLocationDisplay, buildDirectoryUrl } from '../lib/slugs';
import { buildFilteredSearchUrl } from '../lib/searchNavigation';
import { DEFAULT_STATE, LAUNCHED_STATES, stateSlugToDisplay, stateDisplayToSlug, buildStateHubUrl } from '../lib/stateCore.mjs';
import { LocationSelection } from '../types';
import { Button } from '../src/components/ui/button';
import { Input } from '../src/components/ui/input';
import { Combobox, ComboboxOption } from '../src/components/ui/combobox';
import { PageContainer } from '../src/components/ui/page-container';
import { LabelText } from '../src/components/ui/typography';
import PostCard from '../src/components/blog/PostCard';
import PostCardSkeleton from '../src/components/blog/PostCardSkeleton';
import { getPublishedPosts } from '../services/blogService';
import type { HydratedBlogPost } from '../src/types/blog';
import { toast } from '../src/lib/toast';
import { breakpointQuery, matchesBreakpointUp } from '../lib/breakpoints';
import LiveAnnouncer from '../components/LiveAnnouncer';
import { pageTargetIndex, pageCount } from '../lib/carouselPaging';

// FadeInSection component for scroll-triggered animations
// Only triggers after user has scrolled, not on initial page load
// Includes a 1-second delay after entering viewport before animation starts
const FadeInSection: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const domRef = useRef<HTMLDivElement>(null);
  const delayTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Track if user has scrolled
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setHasScrolled(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection observer to detect when element enters viewport
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Only trigger if user has scrolled AND element is intersecting
          if (entry.isIntersecting && hasScrolled) {
            setIsIntersecting(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const currentRef = domRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasScrolled]);

  // Add 1-second delay after intersection before triggering fade-in
  useEffect(() => {
    if (isIntersecting && !isVisible) {
      delayTimerRef.current = setTimeout(() => {
        setIsVisible(true);
      }, 300); // 300ms delay + 700ms animation = 1 second total
    }

    return () => {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
      }
    };
  }, [isIntersecting, isVisible]);

  return (
    <div
      ref={domRef}
      className={`${className} transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
    >
      {children}
    </div>
  );
};

// DelayedFadeInSection component for time-delayed animations
// Triggers fade-in after a specified delay (default 2 seconds) from page load
const DelayedFadeInSection: React.FC<{ children: React.ReactNode; className?: string; delayMs?: number }> = ({ children, className = '', delayMs = 1000 }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delayMs);

    return () => clearTimeout(timer);
  }, [delayMs]);

  return (
    <div
      className={`${className} transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
    >
      {children}
    </div>
  );
};

interface LandingPageProps {
  onSearch: (filters: FilterState, resolvedDisplay?: string) => void;
}

// Sentinel option ids for the leading items the four hero comboboxes share.
const ANY_OPTION_ID = '__any__';
const CURRENT_LOCATION_ID = '__current_location__';

// Gym experience levels exclude fighter-record terms (Amateur/Pro). Filtered once at
// module scope so the rendered options and keyboard navigation always share one list.
const GYM_EXPERIENCE_OPTIONS = (EXPERIENCE_OPTIONS as readonly string[]).filter(
  (opt) => opt !== 'Amateur' && opt !== 'Pro',
);

// Exported for pages/curatedLinkRegistry.test.ts (UI-27) — the source-level
// registry test enumerates every curated link list. Same for VIC_REGIONS and
// STATE_REGION_GROUPS below.
export const REGIONS = [
  {
    name: 'Sydney',
    suburbs: [
      { name: 'Sydney', postcode: '2000' },
      { name: 'Surry Hills', postcode: '2010' },
      { name: 'Redfern', postcode: '2016' },
      { name: 'Darlinghurst', postcode: '2010' },
      { name: 'Pyrmont', postcode: '2009' }
    ]
  },
  {
    name: 'Eastern Suburbs',
    suburbs: [
      { name: 'Bondi', postcode: '2026' },
      { name: 'Randwick', postcode: '2031' },
      { name: 'Coogee', postcode: '2034' },
      { name: 'Rose Bay', postcode: '2029' },
      // UI-12 (operator-ruled 2026-08-06): was Double Bay — which is absent from
      // the gazetted postcode register, so toAddressedLocationSlug emitted a
      // postcode-less URL that no built page answers (soft-404 for crawlers,
      // open-in-new-tab and pre-hydration clicks; 1 of 30 homepage links).
      // Maroubra chosen over the data-leader Bondi Junction to avoid two
      // "Bondi…" entries in one five-item column. The register gap itself
      // (double-bay AND bronte both missing) is flagged upstream to Data &
      // Pipeline — this swap fixes the homepage, not the register.
      { name: 'Maroubra', postcode: '2035' }
    ]
  },
  {
    name: 'Inner West',
    suburbs: [
      { name: 'Newtown', postcode: '2042' },
      { name: 'Marrickville', postcode: '2204' },
      { name: 'Leichhardt', postcode: '2040' },
      { name: 'Balmain', postcode: '2041' },
      { name: 'Glebe', postcode: '2037' }
    ]
  },
  {
    name: 'Northern Beaches',
    suburbs: [
      { name: 'Manly', postcode: '2095' },
      { name: 'Brookvale', postcode: '2100' },
      { name: 'Dee Why', postcode: '2099' },
      { name: 'Mona Vale', postcode: '2103' },
      { name: 'Narrabeen', postcode: '2101' }
    ]
  },
  {
    name: 'South Sydney',
    suburbs: [
      { name: 'Cronulla', postcode: '2230' },
      { name: 'Sutherland', postcode: '2232' },
      { name: 'Hurstville', postcode: '2220' },
      { name: 'Rockdale', postcode: '2216' },
      { name: 'Miranda', postcode: '2228' }
    ]
  },
  {
    name: 'Western Sydney',
    suburbs: [
      { name: 'Parramatta', postcode: '2150' },
      { name: 'Blacktown', postcode: '2148' },
      { name: 'Penrith', postcode: '2750' },
      { name: 'Castle Hill', postcode: '2154' },
      { name: 'Liverpool', postcode: '2170' }
    ]
  }
];

/**
 * UI-26 — the Victorian half of the browse module.
 *
 * SELECTION IS EDITORIAL AND NOT MINE: authored by the Content & SEO session
 * (operator-ruled structure/copy split), corpus-derived — all 388 VIC hub
 * suburbs ranked by gym count, then curated. Three of their choices are
 * recorded here because they are decisions, not data:
 *
 *  - METRO-ONLY, matching the NSW module's precedent (which lists no
 *    Newcastle/Wollongong). The excluded regional leaders, ready-made if a
 *    seventh "Regional Victoria" group is ever wanted: Mornington [11],
 *    Shepparton [8], Warrnambool [8], Sunbury [8], Ballarat Central [7],
 *    Bendigo [7].
 *  - Werribee and Point Cook share postcode 3030 — distinct suburbs with
 *    distinct pages. The NSW list already has this shape (Surry Hills and
 *    Darlinghurst are both 2010), so the structure handles it.
 *  - Epping 3076 collides BY NAME with Epping NSW 2121. Harmless here because
 *    every entry carries its postcode into the URL — but it is one of the 19
 *    cross-state name collisions that broke search scoping in UI-01, so it is
 *    named rather than left to be rediscovered.
 *
 * The 30 entries carry 326 of 1,267 VIC gyms (25.7%). Every one was verified
 * BY THIS SESSION before adoption — register-covered in data/suburbPostcodes.mjs
 * AND holding a built page in the deployed artefact: 30 of 30 passed. That check
 * is the standing contract for any future edit to this list.
 */
export const VIC_REGIONS = [
  {
    name: 'Melbourne',
    suburbs: [
      { name: 'Melbourne', postcode: '3000' },
      { name: 'South Melbourne', postcode: '3205' },
      { name: 'Abbotsford', postcode: '3067' },
      { name: 'Richmond', postcode: '3121' },
      { name: 'Clifton Hill', postcode: '3068' }
    ]
  },
  {
    name: 'Bayside',
    suburbs: [
      { name: 'Moorabbin', postcode: '3189' },
      { name: 'Cheltenham', postcode: '3192' },
      { name: 'Seaford', postcode: '3198' },
      { name: 'Mordialloc', postcode: '3195' },
      { name: 'Brighton', postcode: '3186' }
    ]
  },
  {
    name: 'Northern Suburbs',
    suburbs: [
      { name: 'Epping', postcode: '3076' },
      { name: 'Preston', postcode: '3072' },
      { name: 'Brunswick', postcode: '3056' },
      { name: 'Thomastown', postcode: '3074' },
      { name: 'Thornbury', postcode: '3071' }
    ]
  },
  {
    name: 'Eastern Suburbs',
    suburbs: [
      { name: 'Glen Waverley', postcode: '3150' },
      { name: 'Doncaster East', postcode: '3109' },
      { name: 'Ringwood', postcode: '3134' },
      { name: 'Camberwell', postcode: '3124' },
      { name: 'Balwyn North', postcode: '3104' }
    ]
  },
  {
    name: 'South East',
    suburbs: [
      { name: 'Pakenham', postcode: '3810' },
      { name: 'Dandenong', postcode: '3175' },
      { name: 'Berwick', postcode: '3806' },
      { name: 'Narre Warren', postcode: '3805' },
      { name: 'Rowville', postcode: '3178' }
    ]
  },
  {
    name: 'Western Suburbs',
    suburbs: [
      { name: 'Hoppers Crossing', postcode: '3029' },
      { name: 'Ravenhall', postcode: '3023' },
      { name: 'Werribee', postcode: '3030' },
      { name: 'Melton', postcode: '3337' },
      { name: 'Point Cook', postcode: '3030' }
    ]
  }
];

/**
 * UI-10 — the browse module, grouped BY STATE.
 *
 * The module above was six NSW regions with the state implicit, so the homepage
 * offered a Victorian visitor zero Victorian suburbs (measured live: 0 `/vic/`
 * suburb links) and Victoria was reachable only via UI-09's footer hub link.
 * Regions now hang off a state, and every URL/label derives from that state
 * rather than DEFAULT_STATE — so a third state is a data entry here, not a
 * component change.
 *
 * VIC IS DELIBERATELY EMPTY IN THIS COMMIT. Suburb SELECTION is editorial (the
 * operator's structure/copy split); the Content & SEO session owns it and has
 * been asked for a corpus-derived list. An empty group list renders NOTHING —
 * verified by the `.filter(g => g.regions.length)` below — so this ships dark
 * and lights up when their list lands, with no dead links in the interim.
 */
export const STATE_REGION_GROUPS: ReadonlyArray<{
    state: StateSlug;
    regions: typeof REGIONS;
}> = [
    { state: 'nsw', regions: REGIONS },
    { state: 'vic', regions: VIC_REGIONS },
];

/**
 * The carousel's rendered columns, flattened across states — the SINGLE source
 * for both the rendered list and the pagination maths.
 *
 * Two consumers previously computed page counts from `REGIONS.length`. That was
 * correct while the strip was NSW-only, and stays accidentally correct while VIC
 * is empty — but it would silently under-count the day a second state's regions
 * are added, stranding the trailing columns past the last reachable page. Derive
 * both from this, so the count cannot drift from what is rendered.
 */
const CAROUSEL_COLUMNS: ReadonlyArray<{
    region: (typeof REGIONS)[number];
    state: StateSlug;
    /**
     * UI-42: the column's index WITHIN ITS OWN STATE. The strip renders every
     * state's columns (they must all stay in the prerendered HTML — see the render
     * site) but shows only the selected state's, so snap points and page maths must
     * count from the VISIBLE set. A global flattened index would place phone snap
     * points on hidden columns for every state after the first.
     */
    indexInState: number;
}> =
    STATE_REGION_GROUPS
        .filter(group => group.regions.length > 0)
        .flatMap(group => group.regions.map((region, indexInState) => ({ region, state: group.state, indexInState })));

/** Columns belonging to one state — the count the carousel paginates over. */
const columnCountFor = (state: StateSlug) =>
    CAROUSEL_COLUMNS.reduce((n, c) => (c.state === state ? n + 1 : n), 0);

/**
 * The states the selector may offer: those that actually CONTRIBUTE COLUMNS.
 *
 * NOT LAUNCHED_STATES. A state ships "dark" here — launched, but with no curated
 * region list yet (exactly what VIC did until UI-26 landed, and what
 * `pages/LandingPage.regions.test.tsx` pins as a permanent property). Driving the
 * pills off LAUNCHED_STATES would give such a state a pill that selects NOTHING:
 * a blank strip under the heading, and `browseColumnCount === 0` driving
 * `Math.ceil(0 / perPage) - 1 === -1`, which enables the Previous arrow over an
 * empty carousel. Review-caught and reproduced with a third state.
 */
const BROWSE_STATES: ReadonlyArray<StateSlug> = LAUNCHED_STATES.filter((s) => columnCountFor(s) > 0);

/** First render must be deterministic and match the prerendered HTML. */
const INITIAL_BROWSE_STATE: StateSlug =
    BROWSE_STATES.includes(DEFAULT_STATE) ? DEFAULT_STATE : (BROWSE_STATES[0] ?? DEFAULT_STATE);

/**
 * The carousel's VISIBLE columns. Every state's columns are rendered — that is what
 * keeps all suburb links in the prerendered HTML (conditionally-rendered markup does
 * NOT survive prerendering; CSS-hidden markup does) — so every DOM measurement must
 * skip the hidden ones. Reading `container.children` directly would double the page
 * count and measure `offsetLeft: 0` off a display:none column.
 *
 * Module scope so the []-dep scroll effect below is honestly dependency-free.
 * ⚠️ Coupled to the literal `hidden` token emitted at the column render site — if that
 * ever becomes `hidden md:block` or an inline style, this must change with it.
 */
const visibleSuburbColumns = (container: HTMLElement): HTMLElement[] =>
    Array.from(container.children).filter(
        (el) => !(el as HTMLElement).classList.contains('hidden'),
    ) as HTMLElement[];

const FAQS = [
  {
    question: "How do I find a gym?",
    answer: "Enter a suburb or postcode in the search bar. You can refine results by martial arts style, age group and experience level to narrow down gyms that match your preferences."
  },
  {
    question: "Is the directory free to use and do I need an account?",
    answer: "The directory is free to use and does not require an account to browse or search gyms. Creating an account is optional and allows you to save favourite gyms and searches."
  },
  {
    question: "Where does the gym information come from?",
    answer: "Gym information is compiled from publicly available sources and third-party services, including location data and publicly accessible business information. This information is organised to help you compare options more easily."
  },
  {
    question: "Can I book a trial or class through this website?",
    answer: "No. Martial Arts Network does not support bookings or enrolments. The platform provides access to each gym's publicly listed contact details so you can contact gyms directly to enquire about classes, trials, schedules and pricing."
  },
  {
    question: "What does the experience filter mean?",
    answer: (
      <>
        The experience filter is a general guide to help you identify gyms that may suit your current training background:
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Beginner (&lt;1 year): New to training or under one year of experience.</li>
          <li>Intermediate (1-2 years): Consistent training with basic fundamentals established.</li>
          <li>Advanced (3+ years): Several years of training with strong technical foundations.</li>
        </ul>
        <span className="block mt-2">Experience levels are indicative only and may vary between gyms.</span>
      </>
    )
  }
];

const LandingPage: React.FC<LandingPageProps> = ({ onSearch }) => {
  // UI-10: non-default-state suburb links route directly to their own prerendered
  // page (the state-less onSearch path resolves against DEFAULT_STATE).
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // Latest blog posts for the homepage "From the blog" teaser (client-fetched,
  // like /blog itself). Hidden on fetch error or empty result so the homepage
  // never shows a broken/empty section; SSR paints the skeleton and the cards
  // hydrate in (the accepted client-fetch tradeoff for this section).
  const [latestPosts, setLatestPosts] = useState<HydratedBlogPost[]>([]);
  const [blogLoading, setBlogLoading] = useState(true);
  const [blogError, setBlogError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getPublishedPosts(3)
      .then((posts) => { if (!cancelled) { setLatestPosts(posts); setBlogLoading(false); } })
      .catch(() => { if (!cancelled) { setBlogError(true); setBlogLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  // Scroll to top when navigating to LandingPage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.scrollTo(0, 0);
  }, []);

  // --- Filter State ---
  const [filters, setFilters] = useState<FilterState>({
    styles: [],
    ageGroups: [],
    experienceLevels: [],
    suburb: '',
    radius: 10,
    useRadius: false,
    gymName: ''
  });

  // Search-bar comboboxes (suburb / style / age / level). Open state stays here; the
  // shared Combobox primitive owns each field's active-index, refs, outside-click,
  // Escape, and keyboard navigation.
  const [suburbInput, setSuburbInput] = useState('');
  const [suburbSuggestions, setSuburbSuggestions] = useState<LocationSelection[]>([]);
  const [showSuburbSuggestions, setShowSuburbSuggestions] = useState(false);
  const [indexUnavailable, setIndexUnavailable] = useState(false);
  // UI-55 (operator-ruled 2026-08-11): SOME but not all launched states unavailable.
  // Distinct from indexUnavailable (ALL states down) because the two say different
  // things to a visitor: one means "search is broken", the other means "your suburb
  // may be missing but everything you can see is real".
  const [suggestionsPartial, setSuggestionsPartial] = useState(false);

  const [styleInput, setStyleInput] = useState('');
  const [styleSuggestions, setStyleSuggestions] = useState<string[]>([]);
  const [showStyleSuggestions, setShowStyleSuggestions] = useState(false);

  const [showAgeSuggestions, setShowAgeSuggestions] = useState(false);
  const [showLevelSuggestions, setShowLevelSuggestions] = useState(false);

  const [stepsActiveIndex, setStepsActiveIndex] = useState(0);
  const stepsCarouselRef = useRef<HTMLDivElement>(null);

  // Browse-by-Suburb pager: region columns show two-at-a-time on phones and
  // three-at-a-time from md up (3 slides on phone, 2 on tablet). suburbPerPage
  // tracks the current count (via a matchMedia listener) and drives the dot count
  // + arrow disabled state; suburbActivePage is the active slide.
  const [suburbActivePage, setSuburbActivePage] = useState(0);
  const [suburbPerPage, setSuburbPerPage] = useState(2);
  const suburbCarouselRef = useRef<HTMLDivElement>(null);
  // UI-42: which state's suburb columns are on show. The initial value is chosen
  // during the FIRST render, which IS the prerendered render, so it must be a
  // deterministic module constant — anything else hydrates against markup showing a
  // different state.
  const [browseState, setBrowseState] = useState<StateSlug>(INITIAL_BROWSE_STATE);
  const browseColumnCount = columnCountFor(browseState);
  // Never negative: a zero-column state would otherwise yield maxPage -1 and enable
  // the Previous arrow over an empty strip (review-caught).
  // C2: the DOT COUNT and the SCROLL TARGET now derive from one module, so the row of
  // dots and the thing the arrows scroll to cannot disagree — which is the exact shape
  // of the fault this control already shipped once (12 columns, 3 dots).
  const suburbPageCount = pageCount(browseColumnCount, suburbPerPage);

  /**
   * Switch the browsed state. The carousel is scrolled back to the start because
   * the previous state's scroll offset is meaningless against a different column
   * set — leaving it would land the visitor mid-strip on a page the dots say is
   * page 1.
   */
  const selectBrowseState = (slug: StateSlug) => {
    if (slug === browseState) return;
    setBrowseState(slug);
    setSuburbActivePage(0);
    suburbCarouselRef.current?.scrollTo({ left: 0, behavior: 'auto' });
  };

  // Mobile carousel scroll tracking
  useEffect(() => {
    const handleCarouselScroll = (ref: React.RefObject<HTMLDivElement | null>, setActiveIndex: (index: number) => void) => {
      const container = ref.current;
      if (!container) return;

      const scrollLeft = container.scrollLeft;
      const cardWidth = container.offsetWidth * 0.85; // 85vw cards
      const gap = 16; // gap-4 = 1rem = 16px
      const activeIndex = Math.round(scrollLeft / (cardWidth + gap));
      setActiveIndex(Math.min(Math.max(activeIndex, 0), 2));
    };

    const stepsContainer = stepsCarouselRef.current;
    const onStepsScroll = () => handleCarouselScroll(stepsCarouselRef, setStepsActiveIndex);
    stepsContainer?.addEventListener('scroll', onStepsScroll, { passive: true });

    return () => {
      stepsContainer?.removeEventListener('scroll', onStepsScroll);
    };
  }, []);

  // Keep suburbPerPage (2 on phones, 3 at md+) in sync with the viewport so the
  // dots/arrows and the scroll math agree. Clamp the active slide when the count
  // changes (e.g. rotate to tablet) so it never points past the last slide.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Item 1: the SAME rem query the stylesheet uses for `md:`.
    const mq = window.matchMedia(breakpointQuery('md'));
    const update = () => {
      const perPage = mq.matches ? 3 : 2;
      setSuburbPerPage(perPage);
      // UI-42: paginate over the SELECTED state's columns, not every rendered one.
      const maxPage = Math.max(0, Math.ceil(browseColumnCount / perPage) - 1);
      setSuburbActivePage((p) => Math.min(p, maxPage));
    };
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [browseColumnCount]);

  // Track the active Browse-by-Suburb page from scroll so swiping and the arrow/dot
  // controls stay in sync. Each page == perPage region columns; the stride between
  // two adjacent columns (width + gap) is measured from the DOM so the math never
  // drifts from the CSS. perPage is read live to avoid a stale closure at breakpoint
  // changes.
  useEffect(() => {
    const container = suburbCarouselRef.current;
    if (!container) return;
    const onScroll = () => {
      // UI-42: measure the VISIBLE columns — a hidden one reports offsetLeft 0 and
      // would yield a nonsense stride.
      const cols = visibleSuburbColumns(container);
      const first = cols[0];
      const second = cols[1];
      if (!first || !second) return;
      const stride = second.offsetLeft - first.offsetLeft;
      if (stride <= 0) return;
      const perPage = matchesBreakpointUp('md') ? 3 : 2;
      const maxPage = Math.ceil(cols.length / perPage) - 1;
      const page = Math.round(container.scrollLeft / (stride * perPage));
      setSuburbActivePage(Math.min(Math.max(page, 0), maxPage));
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  // Programmatic carousel navigation for the arrow buttons. Both scroll the
  // existing snap containers and let the scroll listeners recompute the active
  // index/page — no second source of truth. Reduced-motion gets an instant jump.
  const prefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const scrollStepsTo = (index: number) => {
    const container = stepsCarouselRef.current;
    if (!container) return;
    const clamped = Math.min(Math.max(index, 0), 2);
    const cardWidth = container.offsetWidth * 0.85; // 85vw cards
    const gap = 16; // gap-4
    container.scrollTo({ left: clamped * (cardWidth + gap), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  const scrollSuburbToPage = (page: number) => {
    const container = suburbCarouselRef.current;
    if (!container) return;
    const perPage = matchesBreakpointUp('md') ? 3 : 2;
    // UI-42: index into the VISIBLE columns; the hidden state's columns are still
    // children and would offset every target by a whole state's worth.
    const cols = visibleSuburbColumns(container);
    // C2 (2026-08-14): the arithmetic moved to lib/carouselPaging so it can be tested —
    // it sits one line above a scrollTo, and the test environment reports every layout
    // offset as zero, so it was unreachable by any honest test where it was.
    const targetPage = pageTargetIndex(cols.length, perPage, page);
    if (!targetPage) return;
    const first = cols[0];
    const target = cols[targetPage.index];
    if (!first || !target) return;
    container.scrollTo({ left: target.offsetLeft - first.offsetLeft, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  // Snap targets differ per breakpoint: phones page by 2 (snap on even columns),
  // tablets page by 3 (snap on every third). A column can be a snap point on phone
  // but not tablet (and vice-versa), so emit the right responsive snap utilities.
  const suburbSnapClass = (i: number) => {
    const phoneSnap = i % 2 === 0;
    const tabletSnap = i % 3 === 0;
    if (phoneSnap && tabletSnap) return 'snap-start';
    if (phoneSnap) return 'snap-start md:snap-align-none';
    if (tabletSnap) return 'md:snap-start';
    return '';
  };

  // Last reachable slide index for the current column count (phones 2 → 3 slides →
  // max 2; tablets 3 → 2 slides → max 1). Drives the Next-arrow disabled state.
  // UI-42: the selected state's columns only. This ALSO repairs a pre-existing
  // defect: the strip has been 12 columns since VIC's list landed (UI-26), giving 6
  // phone slides, while the dot row below renders a hard-coded 3 — so the last three
  // slides had no dot. One state at a time restores 6 columns, and 6/2 = 3 slides
  // matches the dots again.
  const suburbMaxPage = suburbPageCount - 1;

  // --- Autocomplete Logic ---
  useEffect(() => {
    if (suburbInput.trim().length < 1) {
      setSuburbSuggestions([]);
      return;
    }

    let isCancelled = false;

    const timer = setTimeout(async () => {
      try {
        // UI-28 (operator-ruled): the hero is a BOTH-STATES surface — fan out to
        // EVERY launched state (derived from LAUNCHED_STATES, never hardcoded: two
        // parallel calls today, N at state N+1) and merge. Order is deterministic:
        // LAUNCHED_STATES order, then each service response's own ranking within
        // its block. NEVER dedupe across states — same NAME ≠ same suburb (the
        // Werribee/Point Cook same-postcode lesson, one level up). Each entry
        // carries its source state, and its display string already labels it
        // ("Richmond NSW 2753" vs "Richmond VIC 3121" — the index's convention).
        const unavailableStates = new Set<StateSlug>();
        const perState = await Promise.all(
          LAUNCHED_STATES.map((state) => getLocationSuggestions(suburbInput, state, {
            onUnavailable: (s) => unavailableStates.add(s),
          }))
        );
        const merged = perState.flat();
        if (isCancelled) return;
        suggestionsPartialRef.current = unavailableStates.size > 0;
        // UI-55: the ref already carried this fact but a ref cannot render. Mirror it
        // into state so the dropdown can say so. Deliberately NOT replacing the ref:
        // the Enter-decline below reads it synchronously inside an event handler,
        // where a state value would be a render behind.
        setSuggestionsPartial(unavailableStates.size > 0 && unavailableStates.size < LAUNCHED_STATES.length);

        // Per-state INDEX_UNAVAILABLE (changelog (+11)): one down state no longer
        // wipes the healthy states' entries. Markers are stripped — their states
        // already sit in unavailableStates via the service's unified onUnavailable
        // signal — and the full-outage header shows only when EVERY launched state
        // is down. A partial outage renders the surviving states' labelled entries;
        // Enter auto-navigation still declines through suggestionsPartialRef (the
        // down state might hold the same name — the 19-collision lesson).
        const suggestions = merged.filter(s => s.display !== "INDEX_UNAVAILABLE_MARKER");

        if (unavailableStates.size >= LAUNCHED_STATES.length) {
          setIndexUnavailable(true);
          setSuburbSuggestions([]);
        } else {
          setIndexUnavailable(false);
          setSuburbSuggestions(suggestions);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('[LandingPage] Suggestions failed:', error);
        }
      }
    }, 200);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [suburbInput]);

  // --- Handlers ---

  // Canonical "Suburb Postcode" for the picked suburb (e.g. "Alexandria 2015"), passed to
  // the directory so its pill shows the postcode from first paint — no search-round-trip
  // flick. Cleared on free typing (a typed suburb has no known postcode until resolved).
  const resolvedDisplayRef = useRef<string>('');

  // UI-28: the state slug of the RESOLVED suburb — set by picking a suggestion (the
  // entry's own state) or by "use my location" (the server-returned state). null =
  // free-typed / unresolved, which keeps today's state-less onSearch path. Cleared on
  // free typing alongside resolvedDisplayRef: a typed suburb has no known state
  // until resolved.
  const resolvedStateSlugRef = useRef<StateSlug | null>(null);
  // UI-28 review hardening: true when the latest suggestions fan-out lost a
  // state's block (transport error or server throttle). A partial merged set is
  // not authoritative, so the Enter rule must never auto-navigate from it — a
  // silently missing state would flip "ambiguous → hold" into "one state →
  // navigate" on the 19 cross-state name collisions (the UI-01 family).
  const suggestionsPartialRef = useRef(false);

  // Allowlist a wire/display state down to a launched slug — never trust an
  // unvalidated value into a URL builder. Returns null for absent or unlaunched.
  const toLaunchedStateSlug = (stateDisplay: LocationSelection['state']): StateSlug | null => {
    if (!stateDisplay) return null;
    const slug = stateDisplayToSlug(stateDisplay);
    return LAUNCHED_STATES.includes(slug) ? slug : null;
  };

  const handleSuburbChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSuburbInput(value);
    setFilters(prev => ({ ...prev, suburb: value }));
    resolvedDisplayRef.current = '';
    resolvedStateSlugRef.current = null;
    setShowSuburbSuggestions(true);
  };

  const selectSuburb = (selection: LocationSelection) => {
    setSuburbInput(selection.display ?? '');
    setFilters(prev => ({ ...prev, suburb: selection.display ?? '' }));
    resolvedDisplayRef.current = (selection.suburb && selection.postcode)
      ? `${selection.suburb} ${selection.postcode}`
      : '';
    // UI-28: carry the ENTRY's own state — with a merged both-states dropdown a
    // picked suggestion is no longer implicitly DEFAULT_STATE.
    resolvedStateSlugRef.current = toLaunchedStateSlug(selection.state);
    setShowSuburbSuggestions(false);
  };

  const handleUseLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          // Resolve to the nearest NSW locality via the GNAF index — no paid
          // Google reverse-geocode, and the homepage no longer loads the Maps
          // geocoding library on this path (item 16).
          const nearest = await resolveNearestLocation(latitude, longitude);
          if (nearest?.display) {
            setSuburbInput(nearest.display);
            setFilters(prev => ({ ...prev, suburb: nearest.display }));
            // nearest.display is "Suburb STATE Postcode"; normalise to canonical
            // "Suburb Postcode" so it matches the postcode-less suburb on the directory.
            resolvedDisplayRef.current = /\b\d{4}\b/.test(nearest.display) ? normalizeLocationDisplay(nearest.display) : '';
            // UI-28: consume the state the server now returns, so Search routes to
            // the RESOLVED state's page. Absent (older function still deployed) →
            // null, which is exactly today's DEFAULT_STATE behaviour — graceful
            // until the functions deploy lands. The service already allowlists the
            // value against LAUNCHED_STATES.
            resolvedStateSlugRef.current = nearest.stateSlug ?? null;
            setShowSuburbSuggestions(false);
          } else {
            toast.error("Could not determine a nearby suburb from your location.");
          }
        } catch (err) {
          console.error("Failed to resolve nearest location", err);
          toast.error("Location services could not be loaded. Please try again.");
        }
      },
      (error) => {
        console.error("Error getting location", error);
        toast.error("Unable to retrieve your location.");
      }
    );
  };

  const handleStyleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setStyleInput(value);
    setFilters(prev => ({ ...prev, styles: value ? [value] : [] }));

    if (value.length > 0) {
      const filtered = STYLE_OPTIONS.filter(s =>
        s.toLowerCase().includes(value.toLowerCase())
      );
      setStyleSuggestions(filtered);
    } else {
      setStyleSuggestions([...STYLE_OPTIONS]);
    }
    setShowStyleSuggestions(true);
  };

  const selectStyle = (style: string) => {
    setStyleInput(style);
    setFilters(prev => ({ ...prev, styles: style ? [style] : [] }));
    setShowStyleSuggestions(false);
  };

  const selectAge = (age: string) => {
    setFilters(prev => ({ ...prev, ageGroups: age ? [age] : [] }));
    setShowAgeSuggestions(false);
  };

  const selectLevel = (level: string) => {
    setFilters(prev => ({ ...prev, experienceLevels: level ? [level] : [] }));
    setShowLevelSuggestions(false);
  };

  // UI-28: hero navigation for a NON-DEFAULT-STATE resolution. The onSearch path is
  // state-LESS and resolves against DEFAULT_STATE (App.handleLandingSearch), so a
  // second-state suburb cannot go through it — the UI-10 lesson, applied to the
  // hero. Builds the URL with the SAME shared builder App.handleLandingSearch uses
  // (lib/searchNavigation.ts — extracted 2026-08-07, changelog (+11)) but with the
  // ENTRY's state, so the two paths cannot drift.
  const navigateToStateSearch = (searchFilters: FilterState, state: StateSlug, resolvedDisplay?: string) => {
    const fullUrl = buildFilteredSearchUrl(searchFilters, state);
    navigate(fullUrl, resolvedDisplay ? { state: { resolvedDisplay } } : undefined);
  };

  /**
   * ⚠️⚠️ UI-130 (loading spec RULED B1) — THIS FLAG IS NEVER SET BACK TO FALSE, AND THAT
   * IS CORRECT. Read this before "fixing" the missing reset.
   *
   * B1 made acknowledgement unconditional, including this hero. But `handleSearchClick`
   * is SYNCHRONOUS — it navigates and returns — so there is no in-flight state to bind a
   * spinner to and one had to be created. What it acknowledges is the PRESS, and the
   * press is always followed by a route change: `handleLandingSearch` in App.tsx calls
   * `navigate(...)` on BOTH branches (a filtered url, or `/gyms`), so there is no path
   * where the button stays on screen having done nothing.
   *
   * ⭐ TRACED BEFORE RELYING ON IT, because a never-reset flag on a surface that SURVIVED
   * would strand the button forever: `LandingPage` is a plain `<Route element>` in
   * App.tsx, NOT a persistent overlay like DirectoryPage. It unmounts on navigation and
   * remounts fresh on Back, so the state cannot leak into a later visit.
   *
   * ⚠️ AND WHAT THIS IS REALLY WORTH, stated so nobody deletes it as decoration: the
   * spinner itself is usually invisible — the destination replaces this page within a
   * frame. The load-bearing half is `disabled`, which stops a double-click firing two
   * navigations. The operator was told the spinner would rarely be seen and ruled
   * spinner-AND-lock anyway, because a rule with an exception is a rule nobody can apply.
   */
  const [isSearchPending, setIsSearchPending] = useState(false);

  const handleSearchClick = () => {
    setIsSearchPending(true);
    // UI-28: a suburb resolved to a non-default launched state routes to ITS state's
    // page. Everything else (default state, free-typed, empty) keeps today's
    // state-less onSearch path exactly.
    const resolvedState = resolvedStateSlugRef.current;
    if (filters.suburb && resolvedState && resolvedState !== DEFAULT_STATE) {
      navigateToStateSearch(filters, resolvedState, resolvedDisplayRef.current || undefined);
      return;
    }
    onSearch(filters, resolvedDisplayRef.current || undefined);
  };

  // UI-28 ENTER-WITHOUT-PICKING RULE (operator-ruled, implemented exactly):
  // resolve the typed name against the CURRENT merged suggestions.
  //   - exactly ONE launched state → navigate to that state's page;
  //   - MORE THAN ONE → do NOT navigate; keep the dropdown open showing the
  //     state-labelled options so the user must pick;
  //   - NONE → today's behaviour unchanged (Enter on this field was a no-op).
  // "Resolves" = an exact match on the suburb name, "name postcode", or the full
  // labelled display — a bare prefix or a lone postcode resolves nothing.
  const handleSuburbEnterKey = () => {
    const typed = suburbInput.trim().toLowerCase();
    if (!typed) return;

    const matches = suburbSuggestions.filter((s) => {
      const name = (s.suburb ?? '').toLowerCase();
      if (!name) return false;
      return typed === name
        || (s.postcode ? typed === `${name} ${s.postcode}`.toLowerCase() : false)
        || typed === (s.display ?? '').toLowerCase();
    });

    // Partial fan-out (a state's block missing): the merged set cannot prove
    // "exactly one state" — hold whatever is visible, never auto-navigate.
    if (suggestionsPartialRef.current) {
      if (matches.length > 0) setShowSuburbSuggestions(true);
      return;
    }

    const matchedStates = new Set(matches.map((m) => m.state));
    if (matchedStates.size === 1) {
      // Same name twice within ONE state normalises to one directory URL (the
      // builder strips the postcode and re-addresses from the register), so the
      // first match in merged order is representative.
      const match = matches[0]!;
      selectSuburb(match);
      const resolvedDisplay = (match.suburb && match.postcode) ? `${match.suburb} ${match.postcode}` : undefined;
      // setFilters above is async — build the navigated filters explicitly, as the
      // browse-module handler does.
      const nextFilters: FilterState = { ...filters, suburb: match.display ?? '' };
      const matchState = toLaunchedStateSlug(match.state);
      if (matchState && matchState !== DEFAULT_STATE) {
        navigateToStateSearch(nextFilters, matchState, resolvedDisplay);
      } else {
        onSearch(nextFilters, resolvedDisplay);
      }
      return;
    }
    if (matchedStates.size > 1) {
      // Ambiguous across states: hold the dropdown open — its entries are already
      // state-labelled (the display string carries the state token).
      setShowSuburbSuggestions(true);
    }
  };


  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // --- Hero search-bar combobox option lists ---
  // Each list leads with a sentinel item (Current location / "Any X"); the rest are
  // the field's real options. The Combobox treats the leading item as option index 0.
  const suburbOptions: ComboboxOption<LocationSelection | null>[] = [
    { id: CURRENT_LOCATION_ID, value: null },
    ...suburbSuggestions.map((s, i) => ({ id: s.display ?? `suburb-${i}`, value: s })),
  ];
  const styleOptions: ComboboxOption<string>[] = [
    { id: ANY_OPTION_ID, value: '' },
    ...styleSuggestions.map((s) => ({ id: s, value: s })),
  ];
  const ageOptions: ComboboxOption<string>[] = [
    { id: ANY_OPTION_ID, value: '' },
    ...AGE_OPTIONS.map((a) => ({ id: a, value: a })),
  ];
  const levelOptions: ComboboxOption<string>[] = [
    { id: ANY_OPTION_ID, value: '' },
    ...GYM_EXPERIENCE_OPTIONS.map((e) => ({ id: e, value: e })),
  ];

  const optionRowBase = 'cursor-pointer select-none relative py-3 pl-4 pr-9 transition-colors';

  // UI-50: this root was a plain <div>, so the homepage exposed no main landmark and
  // the skip link had no target here. It carries only page content — the header and
  // footer are rendered by App, not by this component — so promoting it to <main> is
  // the whole change.
  return (
    <main id="main-content" className="min-h-screen bg-surface-canvas flex flex-col auto-scrollbar" style={{ fontFamily: 'Inter, sans-serif' }}>
      <SeoHead meta={getStaticPageMetadata('/')!} />

      {/* Hero Section */}
      <div className="relative bg-surface-ink text-white overflow-hidden flex flex-col pt-32 pb-16 lg:pt-36 lg:pb-20">
        {/* Subtle radial glow for text contrast */}
        {/* Gradient uses brand-primary at 8% alpha. The hex is repeated literally
            because complex gradients are clearer as inline CSS than Tailwind
            utility classes. Keep in sync with brand-primary (#2F5D9F) if updated. */}
        <div className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 80% 50% at 50% 40%, rgba(47, 93, 159, 0.08) 0%, transparent 70%)'
          }}>
        </div>

        {/* Content */}
        <div className="flex-grow flex items-center justify-center relative z-10">
          <PageContainer size="narrow" className="text-center px-6">
            <h1 className="text-5xl sm:text-6xl md:text-7xl mb-6 tracking-tight font-semibold text-white leading-[1.1]">
              Find Your Gym{' '}
              <span className="block text-brand-primary-on-dark">Train Your Way</span>
            </h1>

            {/* Operator-ruled 2026-08-16: the two sentences sit on their own lines.
                `block` on the second span rather than a <br>, matching the h1 directly
                above — a <br> is a layout hack a screen reader announces as nothing,
                whereas two blocks read as two phrases at every width. Both remain in ONE
                <p>, so it stays a single paragraph to assistive tech and to crawlers. */}
            {/* ⚠️ max-w-3xl, NOT the previous max-w-2xl, and the 24 pixels are the whole
                reason. MEASURED at 1280 with the shipped type (20px): the first sentence
                needs 696px and max-w-2xl gives 672 — so it wrapped, and the "two lines"
                above rendered as THREE visual rows. max-w-3xl (768px) clears it with room.
                Below sm the viewport is narrower than either value, so this changes nothing
                on a phone: the sentences wrap there regardless, which is correct. */}
            <p className="text-lg sm:text-xl text-text-subtle max-w-3xl mx-auto font-normal leading-relaxed mb-4">
              <span className="block">The most comprehensive martial arts gym directory across NSW and VIC.</span>
              <span className="block">Connecting you to the right place to train.</span>
            </p>
          </PageContainer>
        </div>
      </div>

      {/* Integrated Search Bar Section */}
      <PageContainer className="relative -mt-8 z-20 pb-12 w-full px-6">
        <div className="bg-surface-canvas rounded-3xl lg:rounded-full border border-border-default p-4 lg:p-2 lg:flex lg:items-center lg:divide-x divide-border-default">

            {/* Mobile: Grid Layout */}
            <div className="grid grid-cols-2 gap-4 w-full lg:flex lg:gap-0 lg:items-center">

              {/* Item 10: the audible half of the outage messaging. Mounted HERE, as a
                  sibling of the Combobox rather than inside its panel, so the live
                  region exists before any message does — a region created at the same
                  moment as its text is commonly not announced at all. Covers BOTH
                  messages this slot carries: the full outage and UI-55's one-state
                  partial. The visible panelHeader below is unchanged. */}
              <LiveAnnouncer
                message={
                  indexUnavailable
                    ? 'Location search is temporarily unavailable. Try again shortly.'
                    : suggestionsPartial
                      ? 'Some suburbs may be missing right now — try again shortly.'
                      : ''
                }
              />
              {/* Location Field - Full width on mobile top row */}
              <Combobox
                className="col-span-2 lg:flex-grow lg:flex-[1.5] lg:px-4"
                label="Suburb or postcode suggestions"
                options={suburbOptions}
                onSelect={(value, option) => {
                  if (option.id === CURRENT_LOCATION_ID) handleUseLocation();
                  else if (value) selectSuburb(value);
                }}
                open={showSuburbSuggestions}
                onOpenChange={setShowSuburbSuggestions}
                wrap="circular"
                resetKey={suburbInput}
                panelClassName="left-0 w-full bg-white max-h-80 rounded-xl text-base sm:text-sm ring-1 ring-border-default overflow-hidden shadow-lg"
                listClassName="max-h-60 overflow-auto focus:outline-none custom-scrollbar"
                panelHeader={indexUnavailable ? (
                  <div className="px-4 py-3 text-sm text-text-muted bg-brand-primary-subtle border-b border-border-subtle">
                    Location search is temporarily unavailable. Try again shortly.
                  </div>
                ) : suggestionsPartial ? (
                  // UI-55: a ONE-state outage. Operator-ruled wording — deliberately
                  // state-agnostic, so it stays correct at any number of launched
                  // states and needs only a boolean rather than the slug set.
                  // Before this, a visitor typing a suburb in the down state saw a list
                  // that looked complete, and Enter did nothing at all.
                  <div className="px-4 py-3 text-sm text-text-muted bg-brand-primary-subtle border-b border-border-subtle">
                    Some suburbs may be missing right now — try again shortly.
                  </div>
                ) : null}
                renderInput={({ ref, getInputProps }) => {
                  const inputProps = getInputProps();
                  return (
                    <>
                      <LabelText className="block text-xs font-medium text-text-muted uppercase tracking-wide mb-1 lg:mb-0 lg:hidden">Location</LabelText>
                      <div className="relative flex items-center">
                        <MapPin className="h-5 w-5 text-brand-primary absolute left-3 top-1/2 -translate-y-1/2 z-10" strokeWidth={1.5} />
                        <Input
                          {...inputProps}
                          ref={ref}
                          type="text"
                          className="block w-full pl-10 pr-4 h-14 lg:h-12 border-b lg:border-none border-border-default bg-transparent text-text-primary placeholder-text-subtle focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:border-none focus-visible:outline-none text-base"
                          placeholder="Suburb or Postcode"
                          value={suburbInput}
                          onChange={handleSuburbChange}
                          onFocus={() => setShowSuburbSuggestions(true)}
                          onKeyDown={(e) => {
                            // The combobox owns arrows/Escape/Enter-on-a-highlighted-
                            // option and preventDefaults everything it consumes; only
                            // an UNCONSUMED Enter falls through to the UI-28
                            // Enter-without-picking rule.
                            inputProps.onKeyDown?.(e);
                            if (e.key === 'Enter' && !e.defaultPrevented) handleSuburbEnterKey();
                          }}
                        />
                      </div>
                    </>
                  );
                }}
                renderOption={(opt, { active }) => {
                  if (opt.id === CURRENT_LOCATION_ID) {
                    return (
                      <div className={`${optionRowBase} flex items-center ${suburbSuggestions.length > 0 ? 'border-b border-border-subtle' : ''} ${active ? 'bg-brand-primary-subtle' : 'hover:bg-surface-canvas'}`}>
                        <div className="flex-shrink-0 mr-3 rounded-full p-2 bg-brand-primary-subtle">
                          <Navigation size={16} fill="currentColor" className="text-brand-primary" />
                        </div>
                        <span className="block truncate font-medium text-text-primary">Current location</span>
                      </div>
                    );
                  }
                  return (
                    <div className={`${optionRowBase} flex items-center ${active ? 'bg-brand-primary-subtle' : 'hover:bg-surface-canvas'}`}>
                      <div className="flex-shrink-0 mr-3 bg-surface-raised text-text-muted rounded-full p-2">
                        <MapPin size={16} />
                      </div>
                      <span className="block truncate font-medium">{opt.value?.display}</span>
                    </div>
                  );
                }}
              />

              {/* Style Field - Full width on mobile second row */}
              <Combobox
                className="col-span-2 lg:flex-grow lg:flex-[2] lg:px-4"
                label="Martial art style suggestions"
                options={styleOptions}
                selectedId={styleInput ? styleInput : ANY_OPTION_ID}
                onSelect={(style) => selectStyle(style)}
                open={showStyleSuggestions}
                onOpenChange={setShowStyleSuggestions}
                wrap="circular"
                resetKey={styleInput}
                panelClassName="left-0 w-full bg-white rounded-xl ring-1 ring-border-default shadow-lg overflow-hidden"
                listClassName="max-h-60 overflow-auto focus:outline-none custom-scrollbar text-base sm:text-sm"
                panelFooter={styleSuggestions.length === 0 ? (
                  <div className="cursor-default select-none relative py-2 pl-3 pr-9 text-text-muted italic">No matches</div>
                ) : null}
                renderInput={({ ref, getInputProps }) => (
                  <>
                    <LabelText className="block text-xs font-medium text-text-muted uppercase tracking-wide mb-1 lg:mb-0 lg:hidden">Style</LabelText>
                    <div className="relative flex items-center">
                      <Dumbbell className="h-5 w-5 text-brand-primary absolute left-3 top-1/2 -translate-y-1/2 z-10" strokeWidth={1.5} />
                      <input
                        {...getInputProps()}
                        ref={ref}
                        type="text"
                        className="block w-full pl-10 pr-8 h-14 lg:h-12 border-b lg:border-none border-border-default bg-transparent text-text-primary placeholder-text-subtle focus:outline-none focus:ring-0 text-base truncate"
                        placeholder="Martial Art Style"
                        value={styleInput}
                        onChange={handleStyleChange}
                        onClick={() => {
                          setStyleSuggestions([...STYLE_OPTIONS]);
                          setShowStyleSuggestions(prev => !prev);
                        }}
                      />
                      <ChevronDown className="h-4 w-4 text-text-muted absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </>
                )}
                renderOption={(opt, { active, selected }) => (
                  opt.id === ANY_OPTION_ID ? (
                    <div className={`${optionRowBase} border-b border-border-default ${active || selected ? 'bg-brand-primary/10' : 'hover:bg-brand-primary/10'} ${selected ? 'text-brand-primary' : 'text-text-muted'}`}>
                      <span className="block truncate font-medium">Any Style</span>
                    </div>
                  ) : (
                    <div className={`${optionRowBase} ${selected ? 'text-brand-primary bg-brand-primary/10' : 'text-text-primary'} ${active ? 'bg-brand-primary/10' : 'hover:bg-brand-primary/10'}`}>
                      <span className="block truncate font-medium">{opt.value}</span>
                    </div>
                  )
                )}
              />

              {/* Age Group - Half width on mobile third row */}
              <Combobox
                className="col-span-1 lg:flex-grow lg:flex-[0.6] lg:px-3 lg:min-w-[120px]"
                label="Age group options"
                options={ageOptions}
                selectedId={filters.ageGroups?.[0] ?? ANY_OPTION_ID}
                onSelect={(age) => selectAge(age)}
                open={showAgeSuggestions}
                onOpenChange={setShowAgeSuggestions}
                wrap="circular"
                panelClassName="left-0 w-full bg-white rounded-xl ring-1 ring-border-default shadow-lg overflow-hidden min-w-[150px]"
                listClassName="max-h-60 overflow-auto focus:outline-none text-base sm:text-sm"
                renderInput={({ ref, open, getInputProps }) => (
                  <>
                    <LabelText className="block text-xs font-medium text-text-muted uppercase tracking-wide mb-1 lg:mb-0 lg:hidden">Age</LabelText>
                    <div className="relative flex items-center">
                      <Users className="h-5 w-5 text-brand-primary absolute left-3 top-1/2 -translate-y-1/2 z-10" strokeWidth={1.5} />
                      <input
                        {...getInputProps()}
                        ref={ref}
                        type="text"
                        readOnly
                        aria-autocomplete="none"
                        className="block w-full pl-10 pr-8 h-14 lg:h-12 border-b lg:border-none border-border-default bg-transparent text-text-primary placeholder-text-subtle focus:outline-none focus:ring-0 text-base cursor-pointer truncate"
                        placeholder="Age Group"
                        value={filters.ageGroups?.[0] ?? ''}
                        onClick={() => setShowAgeSuggestions(!showAgeSuggestions)}
                      />
                      <ChevronDown className={`h-4 w-4 text-text-muted absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                    </div>
                  </>
                )}
                renderOption={(opt, { active, selected }) => (
                  opt.id === ANY_OPTION_ID ? (
                    <div className={`${optionRowBase} border-b border-border-default ${selected ? 'text-brand-primary bg-brand-primary/10' : 'text-text-muted'} ${active ? 'bg-brand-primary/10' : 'hover:bg-brand-primary/10'}`}>
                      <span className="block truncate font-medium">Any Age</span>
                    </div>
                  ) : (
                    <div className={`${optionRowBase} ${selected ? 'text-brand-primary bg-brand-primary/10' : 'text-text-primary'} ${active ? 'bg-brand-primary/10' : 'hover:bg-brand-primary/10'}`}>
                      <span className="block truncate font-medium">{opt.value}</span>
                    </div>
                  )
                )}
              />

              {/* Experience Level */}
              <Combobox
                className="col-span-1 lg:flex-grow lg:flex-[2.2] lg:px-4 lg:min-w-[220px]"
                label="Experience level options"
                options={levelOptions}
                selectedId={filters.experienceLevels?.[0] ?? ANY_OPTION_ID}
                onSelect={(level) => selectLevel(level)}
                open={showLevelSuggestions}
                onOpenChange={setShowLevelSuggestions}
                wrap="circular"
                panelClassName="left-0 w-full bg-white rounded-xl ring-1 ring-border-default shadow-lg overflow-hidden min-w-[150px]"
                listClassName="max-h-60 overflow-auto focus:outline-none text-base sm:text-sm"
                renderInput={({ ref, open, getInputProps }) => (
                  <>
                    <LabelText className="block text-xs font-medium text-text-muted uppercase tracking-wide mb-1 lg:mb-0 lg:hidden">Level</LabelText>
                    <div className="relative flex items-center">
                      <Award className="h-5 w-5 text-brand-primary absolute left-3 top-1/2 -translate-y-1/2 z-10" strokeWidth={1.5} />
                      <input
                        {...getInputProps()}
                        ref={ref}
                        type="text"
                        readOnly
                        aria-autocomplete="none"
                        className="block w-full pl-10 pr-8 h-14 lg:h-12 border-b lg:border-none border-border-default bg-transparent text-text-primary placeholder-text-subtle focus:outline-none focus:ring-0 text-base cursor-pointer truncate"
                        placeholder="Experience Level"
                        value={filters.experienceLevels?.[0] ?? ''}
                        onClick={() => setShowLevelSuggestions(!showLevelSuggestions)}
                      />
                      <ChevronDown className={`h-4 w-4 text-text-muted absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                    </div>
                  </>
                )}
                renderOption={(opt, { active, selected }) => (
                  opt.id === ANY_OPTION_ID ? (
                    <div className={`${optionRowBase} border-b border-border-default ${selected ? 'text-brand-primary bg-brand-primary/10' : 'text-text-muted'} ${active ? 'bg-brand-primary/10' : 'hover:bg-brand-primary/10'}`}>
                      <span className="block truncate font-medium">Any Level</span>
                    </div>
                  ) : (
                    <div className={`${optionRowBase} ${selected ? 'text-brand-primary bg-brand-primary/10' : 'text-text-primary'} ${active ? 'bg-brand-primary/10' : 'hover:bg-brand-primary/10'}`}>
                      <span className="block truncate font-medium">{opt.value}</span>
                    </div>
                  )
                )}
              />

              {/* Search Button - Full width on mobile bottom row */}
              <div className="col-span-2 mt-2 lg:mt-0 lg:col-span-1 lg:w-auto lg:pl-2">
                {/* UI-13: shared <Button> primary variant — colours/hover come from the
                    variant; hero-specific sizing, radius and typography preserved via
                    className (px-0 keeps the original zero mobile padding). Focus ring
                    harmonised to the house inset ring (batch 1E precedent). The icon's
                    old mr-2 is now the primitive's gap-2 — same 8px separation. */}
                {/* UI-130 (RULED B1): `isLoading` disables the button and renders the
                    primitive's spinner. The capability was already built and unused here
                    — audit L11 named this exact button. The Search icon stays: the
                    spinner is additive, so the control's identity does not change. */}
                <Button
                  onClick={handleSearchClick}
                  isLoading={isSearchPending}
                  fullWidth
                  className="h-14 lg:h-12 lg:w-auto px-0 lg:px-8 border-0 font-semibold rounded-xl lg:rounded-full transition-all text-lg lg:text-base"
                >
                  <Search className="h-5 w-5" strokeWidth={2.5} />
                  Search
                </Button>
              </div>

            </div>
          </div>
      </PageContainer>

      {/* SECTION 1: How it Works */}
      <DelayedFadeInSection>
        <div className="pt-2 pb-24 bg-surface-canvas">
          <PageContainer className="px-6">
            {/* Centered heading */}
            <div className="text-center mb-12">
              <h2 className="text-2xl font-semibold text-text-primary">Find gyms in three easy steps</h2>
              <p className="text-text-muted mt-2">No account required</p>
            </div>

            {/* Mobile: horizontal carousel, Desktop: 3-column grid */}
            <div
              ref={stepsCarouselRef}
              role="group"
              aria-roledescription="carousel"
              aria-label="Find gyms in three easy steps"
              className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 -mx-6 min-[920px]:grid min-[920px]:grid-cols-3 min-[920px]:gap-8 min-[920px]:overflow-visible min-[920px]:snap-none min-[920px]:pb-0 min-[920px]:mx-0"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {/* Spacer for centering first card on mobile */}
              <div className="flex-shrink-0 w-[calc((100vw-85vw)/2-8px)] min-[920px]:hidden" aria-hidden="true" />
              {/* Card 1: Search your area - UI-faithful search input miniature */}
              <div className="flex-shrink-0 w-[85vw] snap-center flex flex-col items-center text-center min-[920px]:w-auto min-[920px]:snap-align-none min-[920px]:flex-shrink">
                <div className="w-full aspect-[4/3] bg-white rounded-2xl border border-border-default mb-6 flex items-center justify-center overflow-hidden px-6">
                  {/* Miniature search form matching Hero section: location field + Search
                      button. Mirrors Card 2's stacked layout so all three cards carry the
                      same visual weight, and gives the placeholder more room before it clips. */}
                  <div className="flex flex-col gap-3 w-full max-w-[280px] min-[920px]:scale-90 lg:scale-100">
                    {/* Icon pinned left; label centred in the pill via flex-1 text-center
                        balanced by an equal-width spacer on the right. EXCEPTION in the
                        920–1043 grid band: the column is too narrow for icon + spacer +
                        the full placeholder (measured 121px available vs 151px needed at
                        920), so the spacer hides and the padding trims there — the label
                        sits slightly off-centre instead of ellipsizing. The symmetric
                        restore is min-[1044px], NOT lg: at 1024 PageContainer itself
                        steps to lg:px-8 and the symmetric label space is 150.67px —
                        still under the 151px the text needs — and classic-scrollbar
                        platforms lose ~17px more layout width than the media query
                        sees (review REAL-BUG). ≥1044 fits with that allowance. */}
                    <div className="bg-surface-canvas rounded-full border border-border-default px-5 min-[920px]:px-4 min-[1044px]:px-5 py-3 flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-brand-primary flex-shrink-0" strokeWidth={1.5} />
                      <span className="flex-1 min-w-0 text-center text-text-muted text-base truncate">Suburb or Postcode</span>
                      <span className="h-5 w-5 flex-shrink-0 min-[920px]:hidden min-[1044px]:block" aria-hidden="true" />
                    </div>
                    <div className="bg-brand-primary rounded-full px-5 py-3 flex items-center gap-2">
                      <Search className="h-5 w-5 text-white flex-shrink-0" strokeWidth={2} />
                      <span className="flex-1 min-w-0 text-center text-white text-base font-medium">Search</span>
                      <span className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                    </div>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">Search your area</h3>
                <p className="text-text-muted text-sm">Enter a suburb or postcode to see nearby gyms.</p>
              </div>

              {/* Card 2: Filter what matters - UI-faithful filter fields miniature */}
              <div className="flex-shrink-0 w-[85vw] snap-center flex flex-col items-center text-center min-[920px]:w-auto min-[920px]:snap-align-none min-[920px]:flex-shrink">
                <div className="w-full aspect-[4/3] bg-white rounded-2xl border border-border-default mb-6 flex items-center justify-center overflow-hidden px-6">
                  {/* Miniature filter inputs matching Hero section dropdowns */}
                  <div className="flex flex-col gap-3 w-full max-w-[280px] min-[920px]:scale-90 lg:scale-100">
                    <div className="bg-surface-canvas rounded-full border border-border-default px-5 py-3 flex items-center">
                      <Dumbbell className="h-5 w-5 text-brand-primary flex-shrink-0" strokeWidth={1.5} />
                      <span className="ml-3 text-text-primary text-base font-medium flex-1">BJJ</span>
                      <ChevronDown className="h-4 w-4 text-text-muted flex-shrink-0" />
                    </div>
                    <div className="bg-surface-canvas rounded-full border border-border-default px-5 py-3 flex items-center">
                      <Users className="h-5 w-5 text-brand-primary flex-shrink-0" strokeWidth={1.5} />
                      <span className="ml-3 text-text-primary text-base font-medium flex-1">Adults (18+)</span>
                      <ChevronDown className="h-4 w-4 text-text-muted flex-shrink-0" />
                    </div>
                    <div className="bg-surface-canvas rounded-full border border-border-default px-5 py-3 flex items-center">
                      <Award className="h-5 w-5 text-brand-primary flex-shrink-0" strokeWidth={1.5} />
                      <span className="ml-3 text-text-primary text-base font-medium flex-1">Beginner</span>
                      <ChevronDown className="h-4 w-4 text-text-muted flex-shrink-0" />
                    </div>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">Filter what matters</h3>
                <p className="text-text-muted text-sm">Filter by martial art style, age group or experience level.</p>
              </div>

              {/* Card 3: Review gym profiles - UI-faithful GymCard miniature */}
              <div className="flex-shrink-0 w-[85vw] snap-center flex flex-col items-center text-center min-[920px]:w-auto min-[920px]:snap-align-none min-[920px]:flex-shrink">
                <div className="w-full aspect-[4/3] bg-white rounded-2xl border border-border-default mb-6 flex items-center justify-center overflow-hidden px-4">
                  {/* Miniature gym card matching GymCard component */}
                  <div className="w-[85%] max-w-[320px] rounded-xl border border-border-default bg-white overflow-hidden shadow-sm min-[920px]:scale-90 lg:scale-100">
                    {/* 2:1 image placeholder — matches GymCard's aspect-[2/1] */}
                    <div className="relative w-full aspect-[2/1] bg-surface-canvas flex items-center justify-center">
                      <ImageOff size={24} className="text-text-muted" strokeWidth={1.5} />
                    </div>
                    {/* Content: title row, then suburb + rating row. Mirrors GymCard's
                        layout in skeleton form — no location pin, no favourite heart. */}
                    <div className="p-4">
                      <div className="h-4 bg-text-primary rounded w-4/5 mb-2"></div>
                      <div className="flex items-end justify-between">
                        <div className="h-3 bg-border-default rounded w-16"></div>
                        <div className="flex items-center flex-shrink-0 ml-2">
                          <Star size={14} className="text-state-warning fill-state-warning mr-1" strokeWidth={1.5} />
                          <span className="text-xs font-bold text-text-primary leading-none">4.8</span>
                          <span className="text-[11px] text-text-muted leading-none ml-1">(124)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">Review gym profiles</h3>
                <p className="text-text-muted text-sm">View gym photos, details and contact information.</p>
              </div>
              {/* Spacer for centering last card on mobile */}
              <div className="flex-shrink-0 w-[calc((100vw-85vw)/2-8px)] min-[920px]:hidden" aria-hidden="true" />
            </div>

            {/* Mobile carousel controls: prev/next arrows flanking the decorative
                dots. Dots are aria-hidden; the arrows carry the accessible labels. */}
            <div className="flex items-center justify-center gap-4 mt-4 min-[920px]:hidden">
              <button
                type="button"
                onClick={() => scrollStepsTo(stepsActiveIndex - 1)}
                disabled={stepsActiveIndex === 0}
                aria-label="Previous step"
                className="flex items-center justify-center min-h-[44px] min-w-[44px] rounded-full border border-border-default bg-surface-base text-text-primary transition-colors group hover:bg-border-default disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
              >
                <ChevronLeft size={22} aria-hidden="true" className="group-disabled:opacity-40" />
              </button>
              <div className="flex gap-2" aria-hidden="true">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-colors ${stepsActiveIndex === i ? 'bg-brand-primary' : 'bg-border-default'}`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={() => scrollStepsTo(stepsActiveIndex + 1)}
                disabled={stepsActiveIndex === 2}
                aria-label="Next step"
                className="flex items-center justify-center min-h-[44px] min-w-[44px] rounded-full border border-border-default bg-surface-base text-text-primary transition-colors group hover:bg-border-default disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
              >
                <ChevronRight size={22} aria-hidden="true" className="group-disabled:opacity-40" />
              </button>
            </div>
          </PageContainer>
        </div>
      </DelayedFadeInSection>



      {/* Browse by Suburb Section */}
      <FadeInSection>
        <div className="py-24 bg-surface-canvas">
          <PageContainer className="px-6">
            <div className="mb-10">
              {/* UI-42: heading + the state selector share a row from md up (measured:
                  the heading is 212px and the pills ~132px, so at 375px they need
                  360px against 327px available — they do NOT fit on phones, where the
                  pills take their own full-width row directly above the columns).
                  The heading stays state-NEUTRAL (operator-ruled 2026-08-05); the pills
                  now carry the state signal, which is what that ruling was waiting for. */}
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-text-primary mb-2">Browse by Suburb</h2>
                  <p className="text-text-muted text-sm">Explore gyms in popular regions</p>
                </div>
                {/* Derived from LAUNCHED_STATES, never literals — a third state adds a
                    pill with no code change here (the SITE-6 class). */}
                <div
                  role="group"
                  aria-label="Choose a state to browse"
                  // Count-agnostic: `flex-1` splits the phone row evenly however many
                  // states there are (a hard-coded grid-cols-2 would orphan a third
                  // pill onto its own row), and from md up they shrink to content.
                  className="flex flex-wrap gap-2 md:flex-nowrap md:flex-shrink-0"
                >
                  {BROWSE_STATES.map((slug) => {
                    const isActive = slug === browseState;
                    return (
                      <button
                        key={slug}
                        type="button"
                        aria-pressed={isActive}
                        onClick={() => selectBrowseState(slug)}
                        // focus ring: the ACTIVE pill is filled brand-primary, so a
                        // brand-primary ring on it is invisible — the precise WCAG
                        // 2.4.7 defect UI-37 fixed app-wide in the Button primitive.
                        // `ring-offset-2` restores a boundary (and matches the pager
                        // arrows in this same section, which already carry it).
                        // `border` on BOTH variants (transparent when active) so the two
                        // pills share a box model: without it the active pill measured
                        // 2px narrower and every state switch nudged both pills' widths.
                        className={`flex-1 md:flex-none min-h-[44px] px-5 rounded-full border text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 ${isActive
                          ? 'bg-brand-primary text-white border-transparent'
                          : 'bg-surface-base text-text-primary border-border-default hover:border-text-subtle'
                          }`}
                      >
                        {stateSlugToDisplay(slug)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div
              ref={suburbCarouselRef}
              role="group"
              aria-roledescription="carousel"
              aria-label="Browse by suburb"
              className="hide-scrollbar flex overflow-x-auto snap-x snap-mandatory gap-6 pb-4 -mx-6 pl-6 pr-6 lg:overflow-visible lg:snap-none lg:pb-0 lg:mx-0 lg:pl-0 lg:pr-0 lg:flex-nowrap lg:justify-between lg:gap-y-8"
              style={{ scrollPaddingLeft: '24px' }}
            >
              {/* UI-10: flattened over STATE_REGION_GROUPS so regions from EVERY
                  launched state appear in one strip. `.filter(g => g.regions.length)`
                  means a state with no authored suburbs (VIC today, pending the copy
                  session's list) renders NOTHING rather than an empty column — so this
                  ships dark and lights up on data alone.
                  ⚠️ UI-42 REVERSED THIS BLOCK'S LAST SENTENCE. It used to say the
                  carousel index must stay GLOBAL across the flattened list. That was
                  right while every column was visible; now that one state is shown at
                  a time, snap points must count from the VISIBLE set — hence
                  `indexInState`. A global index would place phone snap points on
                  hidden columns for every state after the first. Rationale in full on
                  the CAROUSEL_COLUMNS type. */}
              {CAROUSEL_COLUMNS.map(({ region, state, indexInState }) => (
                <div
                  key={`${state}-${region.name}`}
                  // UI-42 — THE LOAD-BEARING LINE. The unselected state's columns are
                  // CSS-HIDDEN, never conditionally rendered. Conditional rendering
                  // would strip ~30 suburb links from the prerendered HTML (proven on
                  // the built artefact: a conditionally-rendered popup emits 0
                  // occurrences, while CSS-hidden markup ships intact). The operator
                  // ruled crawlable-link preservation a HARD CONSTRAINT, and the
                  // acceptance test is exact: the homepage must still serve 30 NSW +
                  // 30 VIC suburb links. Do NOT "optimise" this into a ternary.
                  // display:none also takes the hidden columns out of the tab order
                  // and the accessibility tree, so only the shown state is reachable.
                  className={`flex-shrink-0 w-[calc(50vw-36px)] md:w-[calc(33.333vw-32px)] ${suburbSnapClass(indexInState)} lg:w-auto lg:min-w-[120px] lg:flex-shrink ${state === browseState ? '' : 'hidden'}`}
                >
                  <h3 className="font-semibold text-text-primary text-base mb-4">{region.name}</h3>
                  <ul className="space-y-3">
                    {region.suburbs.map(s => (
                      <li key={s.name}>
                        {/* H6: a real <a href> — this was a <button>, so the homepage (the
                            site's strongest page) offered crawlers NO path to the suburb
                            directories: 14 buttons against 2 links. The href points at the
                            suburb's own prerendered directory page, built with name+postcode
                            so the slug matches that route exactly (toLocationSlug strips the
                            postcode, as it does for gym urls).
                            The click handler is UNCHANGED and still wins for real users —
                            preventDefault keeps the instant in-app search instead of a full
                            page load. Modifier-clicks (cmd/ctrl/middle) fall through to the
                            href, so "open in new tab" now works where it previously did
                            nothing. */}
                        <a
                          // UI-10: URL and labels derive from the REGION'S OWN state, not
                          // DEFAULT_STATE — a VIC suburb here must link to /vic/…, and its
                          // in-app search must be scoped to VIC (the UI-01 lesson: an
                          // unscoped search on a second-state suburb returns the wrong
                          // state's gyms, or nothing).
                          href={buildDirectoryUrl(`${s.name} ${s.postcode}`, state)}
                          onClick={(e) => {
                            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                            e.preventDefault();
                            const stateDisplay = stateSlugToDisplay(state);
                            const fullSuburb = `${s.name} ${stateDisplay} ${s.postcode}`;
                            const selection: LocationSelection = {
                              state: stateDisplay,
                              suburb: s.name,
                              postcode: s.postcode,
                              display: fullSuburb
                            };
                            selectSuburb(selection);
                            // A non-default state's suburb cannot go through the state-LESS
                            // onSearch path (it lands on /gyms, which resolves against
                            // DEFAULT_STATE). Route straight to the suburb's own prerendered
                            // page instead — same destination the href names, so the SPA and
                            // the anchor agree.
                            if (state !== DEFAULT_STATE) {
                              navigate(buildDirectoryUrl(`${s.name} ${s.postcode}`, state));
                              return;
                            }
                            onSearch({
                              ...filters,
                              suburb: fullSuburb,
                              location_entry_id: null,
                              location_source: null
                            }, `${s.name} ${s.postcode}`);
                          }}
                          className="text-text-muted hover:text-brand-primary hover:underline text-sm text-left block w-full transition-colors"
                        >
                          {s.name}
                        </a>
                      </li>
                    ))}
                    <li>
                      {/* UI-41 (review catch): each column's "View more" goes to ITS OWN
                          state's hub — this was DEFAULT_STATE_HUB_PATH, sending every VIC
                          column's viewers (and crawlers) to the NSW hub. */}
                      <a href={buildStateHubUrl(state)} className="text-brand-primary text-sm font-medium hover:underline inline-flex items-center mt-1">
                        View more <ChevronRight size={14} className="ml-1" />
                      </a>
                    </li>
                  </ul>
                </div>
              ))}
            </div>

            {/* Pager controls: two regions/slide on phones, three from md up.
                Hidden once the full region row returns at lg. Dots are aria-hidden
                (the third is hidden at md, where there are only two slides). */}
            <div className="flex items-center justify-center gap-4 mt-8 lg:hidden">
              <button
                type="button"
                onClick={() => scrollSuburbToPage(suburbActivePage - 1)}
                disabled={suburbActivePage === 0}
                aria-label="Previous regions"
                className="flex items-center justify-center min-h-[44px] min-w-[44px] rounded-full border border-border-default bg-surface-base text-text-primary transition-colors group hover:bg-border-default disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
              >
                <ChevronLeft size={22} aria-hidden="true" className="group-disabled:opacity-40" />
              </button>
              {/* UI-42: DERIVED, was a hard-coded [0,1,2] with an `md:hidden` on the
                  third. That literal was authored for a 6-column NSW-only strip and
                  went silently wrong the day VIC doubled it to 12 (6 phone slides, 3
                  dots — the last three unreachable). Restoring one state at a time
                  makes 3 correct again TODAY, but only by coincidence of 6 columns:
                  an 8-region list from the copy session would break it from the other
                  direction. Deriving removes the coincidence. Hydration-safe because
                  suburbPerPage initialises to 2 on both server and first client
                  render; the md effect corrects it afterwards. */}
              <div className="flex gap-2" aria-hidden="true">
                {Array.from({ length: suburbPageCount }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-colors ${suburbActivePage === i ? 'bg-brand-primary' : 'bg-border-default'}`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={() => scrollSuburbToPage(suburbActivePage + 1)}
                disabled={suburbActivePage >= suburbMaxPage}
                aria-label="Next regions"
                className="flex items-center justify-center min-h-[44px] min-w-[44px] rounded-full border border-border-default bg-surface-base text-text-primary transition-colors group hover:bg-border-default disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
              >
                <ChevronRight size={22} aria-hidden="true" className="group-disabled:opacity-40" />
              </button>
            </div>
          </PageContainer>
        </div>
      </FadeInSection>

      {/* From the blog — latest 3 posts (client-fetched; hidden on error/empty so
          the homepage never shows a broken/empty section). bg-surface-canvas to
          match the other content sections (How it works / Browse by Suburb). */}
      {(blogLoading || (!blogError && latestPosts.length > 0)) && (
        <FadeInSection>
          <div className="py-24 bg-surface-canvas">
            <PageContainer className="px-6">
              <div className="mb-10 flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:gap-4">
                <div>
                  <h2 className="text-2xl font-semibold text-text-primary mb-2">Martial Arts Network Blog</h2>
                  <p className="text-text-muted text-sm">Guides and tips for getting started and finding your gym</p>
                </div>
                <a href="/blog" className="text-brand-primary text-sm font-medium hover:underline inline-flex items-center flex-shrink-0">
                  View all <ChevronRight size={14} className="ml-1" />
                </a>
              </div>
              {/* Blog cards go 3-across from md (tablet): 3 compact cards (~241px) fill
                  one clean row — chosen over the full-width stack (too large on tablet)
                  and the 2-col 2+1 orphan. Matches the blog-post related-posts grid. */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" aria-busy={blogLoading}>
                {blogLoading ? (
                  <>
                    <span className="sr-only" role="status" aria-label="Loading latest articles" />
                    {Array.from({ length: 3 }).map((_, i) => <PostCardSkeleton key={i} />)}
                  </>
                ) : (
                  latestPosts.map((post) => <PostCard key={post.id} post={post} />)
                )}
              </div>
            </PageContainer>
          </div>
        </FadeInSection>
      )}

      {/* FAQ Section */}
      <div className="py-24 bg-white">
        <PageContainer size="narrow" className="px-6">
          <h2 className="text-2xl font-semibold text-text-primary mb-10">Frequently Asked Questions</h2>
          <div className="border-t border-border-default">
            {FAQS.map((faq, index) => (
              <div key={index} className="border-b border-border-default">
                <button
                  className="w-full py-6 flex justify-between items-center text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary rounded-md group"
                  onClick={() => toggleFaq(index)}
                  aria-expanded={openFaqIndex === index}
                  aria-controls={`faq-answer-${index}`}
                  id={`faq-question-${index}`}
                >
                  <span className={`text-base font-semibold transition-colors pr-8 group-hover:text-brand-primary ${openFaqIndex === index ? 'text-brand-primary' : 'text-text-primary'}`}>{faq.question}</span>
                  {openFaqIndex === index ? (
                    <Minus className="flex-shrink-0 text-text-primary" size={24} strokeWidth={1.5} />
                  ) : (
                    <Plus className="flex-shrink-0 text-text-primary" size={24} strokeWidth={1.5} />
                  )}
                </button>
                {openFaqIndex === index && (
                  <div
                    id={`faq-answer-${index}`}
                    role="region"
                    aria-labelledby={`faq-question-${index}`}
                    className="pb-8 text-text-muted leading-relaxed text-sm whitespace-pre-line"
                  >
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </PageContainer>
      </div>

      {/* Footer */}
    </main>
  );
};

export default LandingPage;